import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
	getMemberById,
	updateMember,
	getTitles,
} from "../../../api/apiServices";
import Loader from "../../Common/Spinner/Loader";

const MemberUpdate = () => {
	let [isLoading, setIsLoading] = useState(true);
	const params = useParams();
	const id = parseInt(params.id);
	const navigate = useNavigate();
	const location = useLocation();
	const from = location.state?.from?.pathname || `/update_member/${id}`;

	const queryClient = useQueryClient();
	const [updateData, setUpdateData] = useState({
		ndcMember: false,
		ndc_membership_number: "",
		voter_id_number: "",
		registered_ndc_member: "",
		title: "",
		last_name: "",
		first_name: "",
		middle_name: "",
		date_of_birth: "",
		mobile_number: "",
		email: "",
		gender: "",
		marital_status: "",
		last_tertiary_institution: "",
		qualification_type: "",
		qualification: "",
		profession: "",
		occupations: "",
		employment_type: "",
		current_employment: "",
	});

	const { data: titles } = useQuery("titles", getTitles);

	const {
		data: member } = useQuery(["member", id], () => getMemberById(id),
		{
			onSuccess: async () => {
				setIsLoading(false);
			},
			onError: async (err) => {
				toast.error(err.message, {
					duration: 5000,
					className: "toast-message",
				});
			},
		}
	);

	const handleOnChange = (e) => {
		const name = e.target.name;
		const value = e.target.value? e.target.value.toLocaleUpperCase(): e.target.value;
		setUpdateData({ ...updateData, [name]: value });
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);

		const data = {
			id: id,
			registered_ndc_member: updateData.registered_ndc_member,
			ndc_membership_number: updateData.ndc_membership_number,
			voter_id_number: updateData.voter_id_number,
			title: updateData.title,
			last_name: updateData.last_name,
			first_name: updateData.first_name,
			middle_name: updateData.middle_name,
			date_of_birth: updateData.date_of_birth,
			gender: updateData.gender,
			marital_status: updateData.marital_status,
			mobile_number: updateData.mobile_number,
			email: updateData.email,
			last_tertiary_institution: updateData.last_tertiary_institution,
			qualification_type: updateData.qualification_type,
			qualification: updateData.qualification,
			profession: updateData.profession,
			occupations: updateData.occupations,
			employment_type: updateData.employment_type,
			current_employment: updateData.current_employment,
		};

		updateMemberMutation.mutate(data);
	};

	const updateMemberMutation = useMutation(updateMember, {
		onSuccess: async (res) => {
			console.log(res)
			if (res.success) {
				setIsLoading(false);
				// Invalidate cached data
				queryClient.invalidateQueries("member");
				toast.success(res.message, {
					duration: 5000,
					className: "toast-message",
				});
				navigate(-1);

			} else {
				toast.error(res.message, {
					duration: 5000,
					className: "toast-message",
				});
				return false;		
			}
		},
		onError: async (err) => {
			toast.error(err.message, {
				duration: 5000,
				className: "toast-message",
			});
			return false;
		},
	});

	const handleCancel = () => {
		navigate("/find_member");
	};

	let updateForm;
	if (isLoading) {
		updateForm = (
			<div className="loader">
				<p>Loading Member Data...</p>
				<Loader />
			</div>
		);
	} else {
		updateForm = (
			<form onSubmit={handleSubmit} key={member.id}>
				<legend style={{ fontWeight: "600", color: "var(--brown)" }}>
					NON-UPDATED INFORMATION
				</legend>
				<div className="form-group row">
					<fieldset className="col-sm-6">
						<div className="form-group row">
							<label
								htmlFor="pf_number"
								className="col-sm-3 col-form-label"
							>
								PF Number
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="pf_number"
									autoComplete="off"
									style={{
										textTransform: "uppercase",
										fontWeight: "bold",
									}}
									onChange={handleOnChange}
									defaultValue={member.pf_number}
									readOnly
									name="pf_number"
									className="form-control"
								/>
							</div>
						</div>
					</fieldset>
				</div>
				<legend style={{ fontWeight: "600", color: "var(--brown)" }}>
					PERSONAL INFORMATION UPDATE
				</legend>
				<div className="form-group row">
					<fieldset className="col-sm-6">
						<div className="form-group row">
							<label
								htmlFor="ndc_membership_number"
								className="col-sm-3 col-form-label"
							>
								NDC Number
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="ndc_membership_number"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									defaultValue={member?.ndc_membership_number}
									name="ndc_membership_number"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="voter_id_number"
								className="col-sm-3 col-form-label"
							>
								Voter ID Number
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="voter_id_number"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									defaultValue={member?.voter_id_number}
									name="voter_id_number"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="title"
								className="col-sm-3 col-form-label"
							>
								Title
							</label>
							<div className="col-sm-9">
								<select
									type="text"
									id="title"
									autoComplete="off"
									onChange={handleOnChange}
									defaultValue={member?.title}
									style={{ textTransform: "uppercase" }}
									name="title"
									className="form-control"
								>
									<option>Select Title</option>
									{titles.map((item, index) => (
										<option value={item.title} key={index}>
											{item.title}
										</option>
									))}
								</select>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="last_name"
								className="col-sm-3 col-form-label"
							>
								Last Name
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="last_name"
									autoComplete="off"
									onChange={handleOnChange}
									defaultValue={member?.last_name}
									style={{ textTransform: "uppercase" }}
									name="last_name"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="first_name"
								className="col-sm-3 col-form-label"
							>
								First Name
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="first_name"
									autoComplete="off"
									onChange={handleOnChange}
									defaultValue={member?.first_name}
									style={{ textTransform: "uppercase" }}
									name="first_name"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="middle_name"
								className="col-sm-3 col-form-label"
							>
								Middle Name
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="middle_name"
									autoComplete="off"
									onChange={handleOnChange}
									defaultValue={member?.middle_name}
									style={{ textTransform: "uppercase" }}
									name="middle_name"
									className="form-control"
								/>
							</div>
						</div>
					</fieldset>
					<fieldset className="col-sm-6">
						<div className="form-group row">
							<label
								htmlFor="date_of_birth"
								className="col-sm-3 col-form-label"
							>
								Date of Birth
							</label>
							<div className="col-sm-9">
								<input
									type="date"
									id="date_of_birth"
									autoComplete="off"
									onChange={handleOnChange}
									value={member?.date_of_birth}
									name="date_of_birth"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="gender"
								className="col-sm-3 col-form-label"
							>
								Gender
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="gender"
									placeholder="gender"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									readOnly
									defaultValue={member?.gender}
									name="gender"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="mobile_number"
								className="col-sm-3 col-form-label"
							>
								Mobile Number
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="mobile_number"
									name="mobile_number"
									defaultValue={member?.mobile_number}
									onChange={handleOnChange}
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="email"
								className="col-sm-3 col-form-label"
							>
								Email Address
							</label>
							<div className="col-sm-9">
								<input
									type="email"
									id="email"
									placeholder="Email Address"
									autoComplete="off"
									onChange={handleOnChange}
									defaultValue={member?.email}
									name="email"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="marital_status"
								className="col-sm-3 col-form-label"
							>
								Marital Status
							</label>
							<div className="col-sm-9">
								<select
									type="text"
									id="marital_status"
									autoComplete="off"
									onChange={handleOnChange}
									defaultValue={member?.marital_status}
									style={{ textTransform: "uppercase" }}
									name="marital_status"
									className="form-control"
								>
									<option>Select Marital Status</option>
									<option value="Single">Single</option>
									<option value="Married">Married</option>
									<option value="Divorced">Divorced</option>
									<option value="Separated">Separated</option>
									<option value="Widowed">Widowed</option>
									<option value="Prefer Not to Say">
										Prefer Not to Say
									</option>
								</select>
							</div>
						</div>
					</fieldset>
				</div>
				<legend style={{ fontWeight: "600", color: "var(--brown)" }}>
					EDUCATIONAL INFORMATION UPDATE
				</legend>
				<div className="form-group row">
					<fieldset className="col-sm-6">
						<div className="form-group row">
							<label
								htmlFor="last_tertiary_institution"
								className="col-sm-3 col-form-label"
							>
								last Tertiary Institution
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="last_tertiary_institution"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									defaultValue={
										member?.last_tertiary_institution
									}
									name="last_tertiary_institution"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="qualification_type"
								className="col-sm-3 col-form-label"
							>
								Last Qualification Type
								<span>&nbsp;&nbsp;*</span>
							</label>
							<div className="col-sm-9">
								<select
									type="text"
									id="qualification_type"
									autoComplete="off"
									onChange={handleOnChange}
									defaultValue={member?.qualification_type}
									style={{ textTransform: "uppercase" }}
									name="qualification_type"
									className="form-control"
								>
									<option value="1st Degree">
										1st Degree
									</option>
									<option value="2nd Degree">
										2nd Degree
									</option>
									<option value="3rd Degree">
										3rd Degree
									</option>
									<option value="Others">Others</option>
								</select>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="qualifications"
								className="col-sm-3 col-form-label"
							>
								Qualifications
							</label>
							<div className="col-sm-9">
								<input
									type="text"
									id="qualifications"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									defaultValue={member?.qualification}
									name="qualifications"
									className="form-control"
								/>
							</div>
						</div>
					</fieldset>
					<fieldset className="col-sm-6">
						<div className="form-group row">
							<label
								htmlFor="profession"
								className="col-sm-2 col-form-label"
							>
								Profession
							</label>
							<div className="col-sm-10">
								<input
									type="text"
									id="profession"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									defaultValue={member?.profession}
									name="profession"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="occupations"
								className="col-sm-2 col-form-label"
							>
								Occupations
							</label>
							<div className="col-sm-10">
								<input
									type="text"
									id="occupations"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									defaultValue={member?.occupations}
									name="occupations"
									className="form-control"
								/>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="employment_type"
								className="col-sm-2 col-form-label"
							>
								Employment Type
							</label>
							<div className="col-sm-10">
								<select
									type="text"
									id="employment_type"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									defaultValue={member?.employment_type}
									name="employment_type"
									className="form-control"
								>
									<option>Select Option</option>
									<option value="Formal">Formal</option>
									<option value="Informal">Informal</option>
								</select>
							</div>
						</div>
						<div className="form-group row">
							<label
								htmlFor="current_employment"
								className="col-sm-2 col-form-label"
							>
								Current Employment{" "}
							</label>
							<div className="col-sm-10">
								<input
									type="text"
									id="current_employment"
									autoComplete="off"
									style={{ textTransform: "uppercase" }}
									onChange={handleOnChange}
									defaultValue={member?.current_employment}
									name="current_employment"
									className="form-control"
								/>
							</div>
						</div>
					</fieldset>
				</div>
				<div className="panel-footer">
					<button
						type="submit"
						className="btn btn-danger"
						onClick={handleCancel}
					>
						Cancel
					</button>
					&nbsp;&nbsp;&nbsp;&nbsp;
					<button type="submit" className="btn btn-success">
						Submit
					</button>
				</div>
			</form>
		);
	}
	return (
		<>
			<section className="content-header">
				<h1>Update Member Information</h1>
			</section>
			<Toaster toastOptions={{ position: "top-center" }} />
			<section className="content">
				<div className="box">
					<div className="box-body">
						<div className="panel panel-default panel-br-4">
							<div className="panel-heading">
								<div className="row">
									<div className="col-sm-10">
										<h4 className="panel-title">
											MEMBER INFORMATION UPDATE
										</h4>
									</div>
									<div className="col-sm-2">
										<button
											style={{ width: "100px" }}
											type="submit"
											className="btn btn-success"
											onClick={handleCancel}
										>
											Exit
										</button>
									</div>
								</div>
							</div>
							<div className="panel-body">{updateForm}</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);
};

export default MemberUpdate;
